/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { BuildOutlined, CodeOutlined, EyeOutlined } from '@ant-design/icons';
import { Segmented } from 'antd';
import { useState } from 'react';

import { ConfigRevision, NodeConfig } from '@/client';
import { ViewMode } from '@/components/Config/utils/types';
import { ConfigPrettyViewer } from '@/components/ConfigPrettyViewer';
import { MimicSyntaxHighlighter } from '@/components/MimicSyntaxHighlighter';
import { useCurrentRole } from '@/hooks/useCurrentRole';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Flex } from '@/primitives/Flex';
import { Link } from '@/primitives/Link';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { tokens } from '@/theme';

export type NodeConfigurationRevisionSourceCodeProps = {
  nodeConfiguration: NodeConfig;
  nodeConfigRevision: ConfigRevision;
  getNodeConfigurationPath: () => string;
};

export function NodeConfigurationRevisionSourceCode({
  nodeConfiguration,
  nodeConfigRevision,
  getNodeConfigurationPath,
}: NodeConfigurationRevisionSourceCodeProps) {
  const { t } = useMimicTranslation('configPrettyViewer');
  const currentRole = useCurrentRole();
  const isSuperadmin = currentRole === 'superadmin';
  const [viewMode, setViewMode] = useState<ViewMode>('pretty');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <span>
        <BuildOutlined />
        <Link
          to={getNodeConfigurationPath()}
          style={{
            marginLeft: '1rem',
            fontSize: '1.1875rem',
            fontFamily: 'DM Mono',
            color: tokens.color.text.textSubtle,
          }}
          dataTestId="node-config-name"
        >
          {nodeConfiguration.name}
        </Link>
      </span>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Flex gap="xxs" />
          <Title
            style={{ fontWeight: 600, padding: '12px 0 24px 0' }}
            level={4}
          >{`Revision #${nodeConfigRevision.revisionNumber} · ${nodeConfigRevision.description}`}</Title>
          <Text type="subtle" style={{ fontWeight: 300, marginBottom: '12px' }} data-testid="revision-id" mono copyable>
            {nodeConfigRevision.id}
          </Text>
        </div>
      </div>

      {/* Only show toggle for superadmins, others get pretty view only */}
      {isSuperadmin && (
        <div style={{ marginBottom: '16px' }}>
          <Flex justify="space-between" align="center">
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                {
                  label: t('prettyView'),
                  value: 'pretty',
                  icon: <EyeOutlined />,
                },
                {
                  label: t('rawCode'),
                  value: 'raw',
                  icon: <CodeOutlined />,
                },
              ]}
            />
          </Flex>
        </div>
      )}

      {/* Show pretty view for everyone, JSON View only for superadmins */}
      {!isSuperadmin || viewMode === 'pretty' ? (
        <ConfigPrettyViewer source={nodeConfigRevision.source} />
      ) : (
        <MimicSyntaxHighlighter source={nodeConfigRevision.source} />
      )}
    </div>
  );
}
